import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  NavLink,
  Redirect,
  Route,
  Switch,
  useParams,
  useRouteMatch,
} from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import {
  AiAgentsNamespaceGetAiAgentArgs,
  Query,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { TableLayoutPlaceholder } from 'src/components/general/feedback/loading-placeholders';
import { FlexColumn } from 'src/components/layout/flex-column';
import { PageContainer } from 'src/components/layout/page-containers';
import { TableLayoutPageContainer } from 'src/components/layout/table-layout-page-container';
import { AiAgentAnalyticsSection } from 'src/components/pages/ai-agents/agents/components/ai-agent-analytics-section';
import { AiAgentKnowledgeSection } from 'src/components/pages/ai-agents/agents/components/ai-agent-knowledge-section';
import { AI_AGENT_DETAIL_QUERY } from 'src/components/pages/ai-agents/agents/queries';
import { Sessions } from 'src/components/pages/ai-agents/agents/sessions/sessions';
import { NotFoundPage } from 'src/components/pages/errors/not-found-page';
import { useAddBreadcrumb } from 'src/hooks/use-add-breadcrumb';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';

const SECTIONS = ['knowledge', 'sessions', 'analytics'] as const;

const DetailsPageContainer = styled(PageContainer)`
  ${up('md')} {
    padding: 20px;
  }
`;

const DetailsLayout = styled(FlexColumn)`
  gap: 20px;

  ${up('md')} {
    align-items: flex-start;
    flex-direction: row;
  }
`;

const SectionsNav = styled(FlexColumn)`
  background: ${({ theme }) => theme.colors.white};
  border: 2px solid ${({ theme }) => theme.colors.white};
  border-radius: 6px;
  flex-shrink: 0;
  overflow: hidden;

  ${up('md')} {
    width: 192px;
  }
`;

const SectionLink = styled(NavLink)`
  align-items: center;
  background-clip: padding-box;
  border-bottom: 2px solid transparent;
  border-top: 2px solid transparent;
  color: ${({ theme }) => theme.colors.flint};
  display: flex;
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 12px;
  line-height: 18px; /* 150% */
  padding: 7px 20px;

  &:hover {
    color: ${({ theme }) => theme.colors.gogo};
  }

  &.active {
    background: ${({ theme }) => theme.colors.secondaryTopaz10};
    box-shadow: inset 2px 0 0 ${({ theme }) => theme.colors.monk};
    color: ${({ theme }) => theme.colors.gogo};
  }
`;

const ContentCard = styled(FlexColumn)`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 6px;
  flex: 1;
  min-width: 0;
  overflow: hidden;

  ${up('md')} {
    min-height: calc(100vh - 120px);
  }
`;

export const AiAgentDetailsPage = memo<{ sectionUrl: string }>(({ sectionUrl }) => {
  const { t } = useTranslation(['aiAgents']);
  const { path, url } = useRouteMatch();
  const { agentId } = useParams<{ agentId: string }>();
  const { id: companyId } = useCurrentInstitution();
  const handleGraphQLError = useHandleGraphQLError();

  const { data, loading } = useQuery<
    Pick<Query, 'aiAgents'>,
    AiAgentsNamespaceGetAiAgentArgs
  >(AI_AGENT_DETAIL_QUERY, {
    variables: { input: { companyId, id: agentId } },
    onError: (error) => handleGraphQLError(error, { isNavigation: true }),
  });
  useAddBreadcrumb(data?.aiAgents.getAIAgent?.name ?? '');

  if (!data || loading) {
    return (
      <TableLayoutPageContainer>
        <TableLayoutPlaceholder />
      </TableLayoutPageContainer>
    );
  }

  if (!data.aiAgents.getAIAgent) {
    return <NotFoundPage />;
  }

  const knowledgeBaseId = data.aiAgents.getAIAgent.privateKnowledgeBase.id;

  return (
    <DetailsPageContainer data-lgg-id="ai-agent-details-page">
      <DetailsLayout>
        <SectionsNav>
          {SECTIONS.map((key) => (
            <SectionLink
              key={key}
              to={`${url}/${key}`}
              data-lgg-id={`ai-agent-details-nav-${key}`}
            >
              {t(`aiAgents:pages.agentDetails.sections.${key}`)}
            </SectionLink>
          ))}
        </SectionsNav>
        <Switch>
          <Route exact path={`${path}/knowledge`}>
            <ContentCard>
              <AiAgentKnowledgeSection
                key={knowledgeBaseId}
                aiAgentId={agentId}
                knowledgeBaseId={knowledgeBaseId}
                sectionUrl={sectionUrl}
              />
            </ContentCard>
          </Route>
          <Route exact path={`${path}/sessions`}>
            <Sessions aiAgentId={agentId} />
          </Route>
          <Route exact path={`${path}/analytics`}>
            <ContentCard>
              <AiAgentAnalyticsSection key={agentId} aiAgentId={agentId} />
            </ContentCard>
          </Route>
          <Redirect to={`${url}/knowledge`} />
        </Switch>
      </DetailsLayout>
    </DetailsPageContainer>
  );
});
